import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputNumber } from 'primeng/inputnumber';
import { Nillable } from '@web/utils';
import { FormControlComponent, PrimengButtonDirective } from '@web/shared/ui';
import { AdminBoxSaleCosmeticAvailableQuantitySubmitEvent } from '../../events';

@Component({
  selector: 'app-admin-box-sale-cosmetic-available-quantity-modal',
  imports: [
    Button,
    Dialog,
    FormControlComponent,
    InputNumber,
    PrimengButtonDirective,
    ReactiveFormsModule,
  ],
  templateUrl:
    './admin-box-sale-cosmetic-available-quantity-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminBoxSaleCosmeticAvailableQuantityModalComponent {
  private readonly builder = inject(FormBuilder);

  public readonly amount = input<Nillable<number>>(1);
  public readonly availableQuantity = input<Nillable<number>>(null);
  public readonly cosmeticName = input.required<string>();
  public readonly visible = input<boolean>(false);
  public readonly cancelEvent = output<void>();
  public readonly submitEvent =
    output<AdminBoxSaleCosmeticAvailableQuantitySubmitEvent>();

  protected readonly form;

  public constructor() {
    this.form = this.buildForm();
    this.initializeFormSynchronizationEffect();
  }

  protected handleVisibleChange(visible: boolean): void {
    if (!visible) {
      this.cancelEvent.emit();
    }
  }

  protected handleSubmitClick(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();

      return;
    }

    this.submitEvent.emit(
      new AdminBoxSaleCosmeticAvailableQuantitySubmitEvent({
        amount: this.form.controls.amount.value as number,
        availableQuantity: this.form.controls.availableQuantity.value as number,
      }),
    );
  }

  private initializeFormSynchronizationEffect(): void {
    effect(() => this.synchronizeFormWithInputs());
  }

  private buildForm() {
    return this.builder.group({
      amount: this.builder.control<Nillable<number>>(1, [
        Validators.required,
        Validators.min(1),
      ]),
      availableQuantity: this.builder.control<Nillable<number>>(null, [
        Validators.required,
        Validators.min(0),
      ]),
    });
  }

  private synchronizeFormWithInputs(): void {
    this.form.controls.amount.setValue(this.amount(), {
      emitEvent: false,
    });
    this.form.controls.amount.markAsPristine();
    this.form.controls.amount.markAsUntouched();
    this.form.controls.availableQuantity.setValue(this.availableQuantity(), {
      emitEvent: false,
    });
    this.form.controls.availableQuantity.markAsPristine();
    this.form.controls.availableQuantity.markAsUntouched();
  }
}
